import React, { Fragment } from 'react';
import {
  SdpFullStatus,
  SdpHasNickname,
  SdpIsNotMandatoryAndConsent,
  SdpResponse,
  headPointNotCapable,
  headPointPhysicalMeter,
} from '../../models/SdpResponse';

import { ReactComponent as CheckedIcon } from '../../assets/checked.svg';
import { ConsumptionUnitEnum } from '../../enum/ConsumptionUnitEnum';
import { DateTime } from 'luxon';
import { ReactComponent as ElectricityIcon } from '../../assets/electricity-meter.svg';
import { ReactComponent as GazIcon } from '../../assets/gas-meter.svg';
import LastUpdateData from '../common/LastUpdateData';
import { MeterTypeEnum } from '../../enum/MeterTypeEnum';
import { ReactComponent as PendingIcon } from '../../assets/pending-meter.svg';
import { SDPDivisionEnum } from '../../enum/SDPDivisionEnum';
import { SDPStatusEnum } from '../../enum/SDPStatusEnum';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export interface OverviewMeterItemProps {
  sdp: SdpResponse;
  isActive: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  onChecked?: (data: {
    event: React.MouseEvent<HTMLDivElement, MouseEvent>;
    sdp: SdpResponse;
  }) => void;
  onClick?: (data: {
    event: React.MouseEvent<HTMLDivElement, MouseEvent>;
    sdp: SdpResponse;
  }) => void;
}
export default function OverviewMeterItem({
  sdp,
  isActive,
  isDisabled,
  isChecked,
  onChecked,
  onClick,
}: OverviewMeterItemProps) {
  const { t, i18n } = useTranslation();

  const renderMeterItemType = () => {
    return (
      <div className="overview-meter-list__item__card__header__left__type">
        {sdp.division === SDPDivisionEnum.ELECTRICITY ? (
          <ElectricityIcon />
        ) : sdp.division === SDPDivisionEnum.GAS ? (
          <GazIcon />
        ) : (
          <PendingIcon />
        )}
        {(headPointNotCapable(sdp.headpoint) || SdpIsNotMandatoryAndConsent(sdp)) &&
          headPointPhysicalMeter(sdp.headpoint)?.type === MeterTypeEnum.SMART && (
            <span className="overview-meter-list__item__card__header__left__type__exclamation">
              !
            </span>
          )}
      </div>
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onClick && !isDisabled) {
      onClick({ event, sdp });
    }
  };

  const handleClickCheckbox = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onChecked) {
      onChecked({ event, sdp });
    }
  };

  const renderMeterItemStatus = () => {
    return (
      <div
        className={classNames('overview-meter-list__item__card__header__status', {
          'overview-meter-list__item__card__header__status--active':
            SdpFullStatus(sdp) === SDPStatusEnum.ACTIVE,
          'overview-meter-list__item__card__header__status--inactive':
            SdpFullStatus(sdp) === SDPStatusEnum.INACTIVE,
          'overview-meter-list__item__card__header__status--expired':
            SdpFullStatus(sdp) === SDPStatusEnum.EXPIRED,
          'overview-meter-list__item__card__header__status--waiting':
            SdpFullStatus(sdp) === SDPStatusEnum.WAITING,
        })}
      >
        {t(SDPStatusEnum.getTranslation(SdpFullStatus(sdp)))}
      </div>
    );
  };

  const renderEmptyStateData = () => {
    return (
      <div className="overview-meter-list__item__card__data__empty-state">
        <div
          className={classNames('overview-meter-list__item__card__data__empty-state__separator', {
            'overview-meter-list__item__card__data__empty-state__separator--active': isActive,
          })}
        ></div>
        <div
          className={classNames('overview-meter-list__item__card__data__empty-state__message', {
            'overview-meter-list__item__card__data__empty-state__message--active': isActive,
          })}
        >
          {t('common.unavailable.short')}
        </div>
      </div>
    );
  };

  const lastDayConsumption = ConsumptionUnitEnum.formatValue(
    ConsumptionUnitEnum.KWH,
    headPointPhysicalMeter(sdp.headpoint)?.lastDayConsumption
  );

  const lastMonthConsumption = ConsumptionUnitEnum.formatValue(
    ConsumptionUnitEnum.KWH,
    headPointPhysicalMeter(sdp.headpoint)?.lastMonthConsumption
  );

  const hasData = () =>
    SdpFullStatus(sdp) !== SDPStatusEnum.WAITING &&
    SdpFullStatus(sdp) !== SDPStatusEnum.EXPIRED &&
    SdpFullStatus(sdp) !== SDPStatusEnum.INACTIVE &&
    sdp.division === SDPDivisionEnum.ELECTRICITY &&
    headPointNotCapable(sdp.headpoint) === false &&
    SdpIsNotMandatoryAndConsent(sdp) === false &&
    ((lastDayConsumption != null && lastMonthConsumption != null) ||
      lastDayConsumption != null ||
      lastMonthConsumption != null);

  return (
    <li className="overview-meter-list__item">
      {onChecked && (
        <div
          className={classNames('overview-meter-list__item__checkbox', {
            'overview-meter-list__item__checkbox--checked': isChecked,
            'overview-meter-list__item__checkbox--disabled': isDisabled,
          })}
          onClick={handleClickCheckbox}
        >
          {isChecked && <CheckedIcon />}
        </div>
      )}
      <div style={{ width: onChecked ? '80%' : '100%' }} onClick={handleClick}>
        <div
          className={classNames('overview-meter-list__item__card', {
            'overview-meter-list__item__card--active': isActive,
            'overview-meter-list__item__card--disabled': !onClick || isDisabled,
          })}
        >
          <div className="overview-meter-list__item__card__header">
            <div className="overview-meter-list__item__card__header__left">
              {renderMeterItemType()}
              <div className="overview-meter-list__item__card__header__left__name">
                <div
                  className={classNames(
                    'overview-meter-list__item__card__header__left__name__nickname',
                    {
                      'overview-meter-list__item__card__header__left__name__nickname--active':
                        isActive,
                    }
                  )}
                >
                  {SdpHasNickname(sdp) ? sdp.userHasAccessNickname : sdp.headpoint.ean}
                </div>
                {SdpHasNickname(sdp) && (
                  <div
                    data-test-id={`overview-meter-item-ean-${sdp.headpoint.ean}`}
                    className={classNames(
                      'overview-meter-list__item__card__header__left__name__ean',
                      {
                        'overview-meter-list__item__card__header__left__name__ean--active':
                          isActive,
                      }
                    )}
                  >
                    {sdp.headpoint.ean}
                  </div>
                )}
              </div>
            </div>
            {renderMeterItemStatus()}
          </div>

          {hasData() && (
            <Fragment>
              <div className="overview-meter-list__item__card__divider"></div>
              <div className="overview-meter-list__item__card__data">
                {lastDayConsumption ? (
                  <div className="overview-meter-list__item__card__data__item">
                    <div
                      className={classNames('overview-meter-list__item__card__data__item__value', {
                        'overview-meter-list__item__card__data__item__value--active': isActive,
                      })}
                    >
                      {lastDayConsumption}
                    </div>
                    <div
                      className={classNames(
                        'overview-meter-list__item__card__data__item__caption',
                        {
                          'overview-meter-list__item__card__data__item__caption--active': isActive,
                        }
                      )}
                    >
                      {DateTime.fromISO(headPointPhysicalMeter(sdp.headpoint)?.lastAvailableDay!)
                        .setLocale(i18n.language)
                        .setZone('Europe/Brussels')
                        .toFormat('EEEE dd/MM/yyyy')}
                    </div>
                  </div>
                ) : (
                  renderEmptyStateData()
                )}
                {lastMonthConsumption ? (
                  <div className="overview-meter-list__item__card__data__item">
                    <div
                      className={classNames('overview-meter-list__item__card__data__item__value', {
                        'overview-meter-list__item__card__data__item__value--active': isActive,
                      })}
                    >
                      {lastMonthConsumption}
                    </div>
                    <div
                      className={classNames(
                        'overview-meter-list__item__card__data__item__caption',
                        {
                          'overview-meter-list__item__card__data__item__caption--active': isActive,
                        }
                      )}
                    >
                      {t('common.this.month')}
                    </div>
                  </div>
                ) : (
                  renderEmptyStateData()
                )}
              </div>
            </Fragment>
          )}
        </div>

        {[SDPStatusEnum.WAITING, SDPStatusEnum.EXPIRED].includes(SdpFullStatus(sdp)) === false && (
          <LastUpdateData date={new Date()} />
        )}
      </div>
    </li>
  );
}
